import React from 'react';
import { Users, Mail, Phone, Shield, Calendar } from 'lucide-react';

const CompanyUsersTab = ({ company }) => {
    // Mock users data for the company
    const mockUsers = [
        {
            id: 1,
            name: company.contactPerson,
            email: company.email,
            phone: company.phone,
            role: 'OWNER',
            status: 'active',
            lastLogin: '2026-01-24T10:30:00',
            joinedDate: company.registeredDate
        },
        {
            id: 2,
            name: 'Admin User',
            email: `admin@${company.email.split('@')[1]}`,
            phone: '+971 50 111 2222',
            role: 'ADMIN',
            status: 'active',
            lastLogin: '2026-01-24T09:15:00',
            joinedDate: company.registeredDate
        }
    ];

    const getRoleBadgeColor = (role) => {
        const colors = {
            'OWNER': 'bg-purple-100 text-purple-700',
            'ADMIN': 'bg-blue-100 text-blue-700',
            'STAFF': 'bg-green-100 text-green-700'
        };
        return colors[role] || 'bg-gray-100 text-gray-700';
    };

    const getStatusBadgeColor = (status) => {
        const colors = {
            'active': 'bg-green-100 text-green-700',
            'inactive': 'bg-gray-100 text-gray-700',
            'suspended': 'bg-red-100 text-red-700'
        };
        return colors[status] || 'bg-gray-100 text-gray-700';
    };

    const formatDateTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="p-6 max-w-6xl mx-auto">
            {/* Header */}
            <div className="mb-6">
                <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    Company Users
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                    Manage users and their roles for {company.name}
                </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-xl shadow-md border border-blue-100/50 p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600 mb-1">Total Users</p>
                            <p className="text-2xl font-bold text-gray-900">{company.userCount}</p>
                        </div>
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                            <Users size={24} className="text-blue-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-md border border-green-100/50 p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600 mb-1">Active Users</p>
                            <p className="text-2xl font-bold text-gray-900">{Math.floor(company.userCount * 0.8)}</p>
                        </div>
                        <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                            <Shield size={24} className="text-green-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-md border border-purple-100/50 p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600 mb-1">Admins</p>
                            <p className="text-2xl font-bold text-gray-900">2</p>
                        </div>
                        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                            <Shield size={24} className="text-purple-600" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-2xl shadow-lg border border-purple-100/50 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-200">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                    User
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                    Contact
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                    Role
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                                    Last Login
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {mockUsers.map((user) => (
                                <tr key={user.id} className="hover:bg-blue-50/30 transition-colors">
                                    <td className="px-4 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                                                <span className="text-white font-bold text-sm">
                                                    {user.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                                                </span>
                                            </div>
                                            <div>
                                                <div className="font-semibold text-sm text-gray-900">{user.name}</div>
                                                <div className="text-xs text-gray-500 flex items-center gap-1">
                                                    <Calendar size={10} />
                                                    Joined {new Date(user.joinedDate).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4">
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-2 text-sm text-gray-700">
                                                <Mail size={12} className="text-gray-400" />
                                                {user.email}
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-gray-700">
                                                <Phone size={12} className="text-gray-400" />
                                                {user.phone}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4">
                                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${getRoleBadgeColor(user.role)}`}>
                                            <Shield size={10} className="mr-1" />
                                            {user.role}
                                        </span>
                                    </td>
                                    <td className="px-4 py-4">
                                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusBadgeColor(user.status)}`}>
                                            {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                                        </span>
                                    </td>
                                    <td className="px-4 py-4">
                                        <div className="text-sm text-gray-700">{formatDateTime(user.lastLogin)}</div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Info Note */}
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-800">
                    <strong>Note:</strong> This is a preview of company users. Full user management features including add, edit, and role assignment will be available in the production version.
                </p>
            </div>
        </div>
    );
};

export default CompanyUsersTab;
